import { PaginationState } from '@tanstack/react-table'
import { useFacilityModelAccess } from 'features/facilityModel/access'
import { Box } from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import { useBoxHover } from 'features/knowledgeDocumentViewer/hooks/useBoxHover'
import { useDocChatStorageOwner } from 'features/knowledgeDocumentViewer/hooks/useDocChatStorageOwner'
import {
  type RegisterTabLeaveGuard,
  useTabLeaveGuards,
} from 'features/knowledgeDocumentViewer/hooks/useTabLeaveGuards'
import { useTabState } from 'features/knowledgeDocumentViewer/hooks/useTabState'
import {
  DOC_METADATA_USER_EDITS_EDIT_PERMISSIONS,
  DOC_METADATA_USER_EDITS_VIEW_PERMISSIONS,
} from 'features/knowledgeDocumentViewer/perms/docMetadataUserEditsPermissionsList'
import {
  DocumentHistoryItem,
  DocumentTab,
  FilterOption,
  type FragmentHighlight,
  type LotoPanelState,
  type RegionHighlight,
  SearchHistoryItem,
  type SearchStateUpdate,
  TabHistoryItem,
  TooltipOption,
  ViewLayout,
} from 'features/knowledgeDocumentViewer/types'
import { DocumentViewerAssetType } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { shouldExitDocumentFullscreenOnLeave } from 'features/knowledgeDocumentViewer/utils/documentFullscreen'
import {
  type MetadataScope,
  readMetadataScopePreference,
  writeMetadataScopePreference,
} from 'features/knowledgeDocumentViewer/utils/metadataScopeStorage'
import { createDefaultSearchHistoryItem } from 'features/knowledgeDocumentViewer/utils/tabStateUrlHelpers'
import {
  getViewerPreferences,
  type LinkHighlightOptions,
  type MinimapPreference,
  updateViewerPreferences,
  VIEWER_PREFERENCES_DEFAULTS,
} from 'features/knowledgeDocumentViewer/utils/viewerPreferencesStorage'
import { useGeckoPermissions } from 'features/permissions'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { DocumentViewerUrlPaths } from 'routes/documents/-searchParams'
import {
  PERMISSION_CANTILEVER_DOCCHAT_REVISIONS_VIEWER,
  PERMISSION_CANTILEVER_POWER_SUBJECTS_VIEWER,
  PERMISSION_GECKO_INTERNAL_MEMBER,
} from 'types'

const MAX_RECENT_VIEWER_ACTIONS = 20

export enum ActionMode {
  None = 'none',
  Assign = 'assign',
  Remove = 'remove',
  Group = 'group',
  Split = 'split',
  Info = 'info',
  Link = 'link',
}

interface KnowledgeDocumentUXContextType {
  actionMode: ActionMode
  setActionMode: (actionMode: ActionMode) => void
  selectedAssetType: DocumentViewerAssetType | undefined
  setSelectedAssetType: (assetType: DocumentViewerAssetType | undefined) => void
  groupDelimiter: string | undefined
  setGroupDelimiter: (delimiter: string | undefined) => void
  hoverId: string | null
  handleMouseEnter: (box: Box) => void
  handleMouseLeave: () => void
  openTabs: DocumentTab[]
  addTab: (tab: DocumentTab) => void
  addDocumentTab: (params: {
    documentId: string
    pageNumber?: number
    selectedBoxIds?: string[]
    query?: string
    searchOriginQuery?: string
    fragmentHighlight?: FragmentHighlight
    regionHighlight?: RegionHighlight
    selectedGraphNodeId?: string
    lotoPanel?: LotoPanelState
  }) => void
  removeTab: (tabId: string, onRemoved?: () => void) => void
  renameTab: (tabId: string, label: string) => void
  reorderTabs: (newTabs: DocumentTab[]) => void
  activeTabId: string
  setActiveTabId: (tabId: string) => void
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  activeHistoryItem: TabHistoryItem
  isOnDocument: boolean
  isOnSearch: boolean
  isOnLinkedDocuments: boolean
  currentLinkedDocumentsData: SearchHistoryItem['linkedDocuments'] | null
  currentDocumentId: string
  currentPageNumber: number
  currentSelectedBoxIds: string[]
  currentDocumentQuery: string
  currentSearchOriginQuery: string
  currentSearchState: SearchHistoryItem['searchState']
  tooltipOptions: TooltipOption[]
  setTooltipOptions: React.Dispatch<React.SetStateAction<TooltipOption[]>>
  linkHighlights: LinkHighlightOptions
  setLinkHighlights: React.Dispatch<React.SetStateAction<LinkHighlightOptions>>
  graphMode: boolean
  setGraphMode: React.Dispatch<React.SetStateAction<boolean>>
  canUsePhotoMode: boolean
  photoMode: boolean
  setPhotoMode: React.Dispatch<React.SetStateAction<boolean>>
  showPhotoCounts: boolean
  togglePhotoCounts: () => void
  showLiveProcessData: boolean
  setShowLiveProcessData: React.Dispatch<React.SetStateAction<boolean>>
  filterOptions: FilterOption
  setFilterOptions: React.Dispatch<React.SetStateAction<FilterOption>>
  assetTypeFilters: DocumentViewerAssetType[]
  setAssetTypeFilters: React.Dispatch<
    React.SetStateAction<DocumentViewerAssetType[]>
  >
  isAdvancedSearchOpen: boolean
  setIsAdvancedSearchOpen: React.Dispatch<React.SetStateAction<boolean>>
  handleSelectDocument: (
    documentId: string,
    query?: string,
    fragmentHighlight?: FragmentHighlight,
    searchOriginQuery?: string,
    pageNumber?: number
  ) => void
  isBrowserFullscreen: boolean
  isDocumentFullscreen: boolean
  setIsDocumentFullscreen: React.Dispatch<React.SetStateAction<boolean>>
  isSidebarCollapsed: boolean
  setIsSidebarCollapsed: React.Dispatch<React.SetStateAction<boolean>>
  viewOnly: boolean
  pushToHistory: (tabId: string, item: TabHistoryItem) => void
  goBack: (tabId: string) => void
  goForward: (tabId: string) => void
  registerTabLeaveGuard: RegisterTabLeaveGuard
  updateDocumentHistoryItem: (
    tabId: string,
    updates: Partial<Omit<DocumentHistoryItem, 'type'>>
  ) => void
  updateSearchHistoryItem: (
    tabId: string,
    searchStateUpdates: SearchStateUpdate
  ) => void
  canGoBack: boolean
  canGoForward: boolean
  canViewGraph: boolean
  isGeckoUser: boolean
  canSeeSubjects: boolean
  /** Viewer or editor of dedicated user-edit roles — hydrate and overlay. */
  canViewUserEdits: boolean
  /** Editor of dedicated user-edit roles — create, edit, and delete. */
  canEditUserEdits: boolean
  viewLayout: ViewLayout
  setViewLayout: React.Dispatch<React.SetStateAction<ViewLayout>>
  minimapPreference: MinimapPreference
  setMinimapPreference: React.Dispatch<React.SetStateAction<MinimapPreference>>
  isLabelerMode: boolean
  setIsLabelerMode: React.Dispatch<React.SetStateAction<boolean>>
  isEvalSessionLocked: boolean
  onExitEvalSession: (() => void) | undefined
  siteId: string
  tabIndicatorColors: Map<string, string>
  setTabIndicatorColors: React.Dispatch<
    React.SetStateAction<Map<string, string>>
  >
  recentViewerActions: string[]
  recordViewerAction: (action: string) => void
  metadataScope: MetadataScope
  setMetadataScope: (scope: MetadataScope) => void
}

interface KnowledgeDocumentUXProviderProps {
  children: React.ReactNode
  basePath: DocumentViewerUrlPaths
  viewOnly: boolean
  siteId: string
  isEvalSessionLocked?: boolean
  tabStateScopeSuffix?: string
  restorePersistedTabs?: boolean
  onExitEvalSession?: () => void
}

export const KnowledgeDocumentUXProvider: React.FC<
  KnowledgeDocumentUXProviderProps
> = (props) => {
  const storageOwner = useDocChatStorageOwner()
  return <UserKnowledgeDocumentUXProvider key={storageOwner} {...props} />
}

const UserKnowledgeDocumentUXProvider: React.FC<
  KnowledgeDocumentUXProviderProps
> = ({
  children,
  basePath,
  viewOnly,
  siteId,
  isEvalSessionLocked = false,
  tabStateScopeSuffix,
  restorePersistedTabs,
  onExitEvalSession,
}) => {
  const tabState = useTabState(basePath, siteId, {
    embedded: viewOnly,
    scopeSuffix: tabStateScopeSuffix,
    restorePersisted: restorePersistedTabs,
  })
  const {
    openTabs,
    activeTabId,
    setActiveTabId,
    addTab,
    addDocumentTab,
    renameTab,
    reorderTabs,
    activeHistoryItem,
    updateDocumentHistoryItem,
    updateSearchHistoryItem,
    canGoBack,
    canGoForward,
  } = tabState
  const { pushToHistory, goBack, goForward, removeTab, registerTabLeaveGuard } =
    useTabLeaveGuards(tabState)

  const { hasPermissions, user } = useGeckoPermissions()
  const isGeckoUser = hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])
  const canViewGraph = hasPermissions([
    PERMISSION_GECKO_INTERNAL_MEMBER,
    PERMISSION_CANTILEVER_DOCCHAT_REVISIONS_VIEWER,
  ])
  const canSeeSubjects = hasPermissions([
    PERMISSION_GECKO_INTERNAL_MEMBER,
    PERMISSION_CANTILEVER_POWER_SUBJECTS_VIEWER,
  ])
  const canViewUserEdits = hasPermissions(
    DOC_METADATA_USER_EDITS_VIEW_PERMISSIONS
  )
  const canEditUserEdits = hasPermissions(
    DOC_METADATA_USER_EDITS_EDIT_PERMISSIONS
  )
  const userId = user?.id

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 200,
  })

  const [persistedUXPrefs] = useState(() =>
    userId ? getViewerPreferences(userId, siteId) : null
  )

  const [actionMode, setActionMode] = useState<ActionMode>(ActionMode.Info)
  const [tooltipOptions, setTooltipOptions] = useState<TooltipOption[]>(
    persistedUXPrefs?.tooltipOptions ?? [TooltipOption.ShowTooltips]
  )
  const [linkHighlights, setLinkHighlights] = useState<LinkHighlightOptions>(
    persistedUXPrefs?.linkHighlights ??
      VIEWER_PREFERENCES_DEFAULTS.linkHighlights
  )
  const [graphModePreference, setGraphMode] = useState<boolean>(
    persistedUXPrefs?.graphMode ?? VIEWER_PREFERENCES_DEFAULTS.graphMode
  )
  const [photoModePreference, setPhotoMode] = useState<boolean>(
    persistedUXPrefs?.photoMode ?? VIEWER_PREFERENCES_DEFAULTS.photoMode
  )
  const { canView: canViewFacilityModel } = useFacilityModelAccess(siteId)
  const canUsePhotoMode = canViewFacilityModel && !isEvalSessionLocked
  const [showPhotoCounts, setShowPhotoCounts] = useState(true)
  const togglePhotoCounts = useCallback(
    () => setShowPhotoCounts((shown) => !shown),
    []
  )
  const [showLiveProcessData, setShowLiveProcessData] = useState<boolean>(
    persistedUXPrefs?.showLiveProcessData ??
      VIEWER_PREFERENCES_DEFAULTS.showLiveProcessData
  )
  const [selectedAssetType, setSelectedAssetType] = useState<
    DocumentViewerAssetType | undefined
  >(undefined)
  const [groupDelimiter, setGroupDelimiter] = useState<string | undefined>(
    undefined
  )
  const [filterOptions, setFilterOptions] = useState<FilterOption>(
    persistedUXPrefs?.filterOptions ?? FilterOption.ShowConfirmed
  )
  const [assetTypeFilters, setAssetTypeFilters] = useState<
    DocumentViewerAssetType[]
  >([])
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] =
    useState<boolean>(false)
  const [tabIndicatorColors, setTabIndicatorColors] = useState<
    Map<string, string>
  >(() => new Map())
  const [recentViewerActions, setRecentViewerActions] = useState<string[]>([])

  const [viewLayout, setViewLayout] = useState<ViewLayout>(
    persistedUXPrefs?.viewLayout ?? 'continuous'
  )
  const [minimapPreference, setMinimapPreference] = useState<MinimapPreference>(
    persistedUXPrefs?.minimapPreference ??
      VIEWER_PREFERENCES_DEFAULTS.minimapPreference
  )

  const [isBrowserFullscreen, setIsBrowserFullscreen] = useState(false)
  const [isDocumentFullscreen, setIsDocumentFullscreen] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    isEvalSessionLocked ? false : (persistedUXPrefs?.isSidebarCollapsed ?? true)
  )
  const [isLabelerModeState, setIsLabelerMode] = useState(false)
  const [metadataScope, setMetadataScopeState] = useState<MetadataScope>(
    () => readMetadataScopePreference() ?? 'doc'
  )
  const setMetadataScope = useCallback((scope: MetadataScope) => {
    setMetadataScopeState(scope)
    writeMetadataScopePreference(scope)
  }, [])

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsBrowserFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  const uxPrefsRef = useRef({
    tooltipOptions,
    filterOptions,
    isSidebarCollapsed,
    viewLayout,
    minimapPreference,
    linkHighlights,
    showLiveProcessData,
    graphMode: graphModePreference,
    photoMode: photoModePreference,
  })
  uxPrefsRef.current = {
    tooltipOptions,
    filterOptions,
    isSidebarCollapsed,
    viewLayout,
    minimapPreference,
    linkHighlights,
    showLiveProcessData,
    graphMode: graphModePreference,
    photoMode: photoModePreference,
  }

  const userIdRef = useRef(userId)
  userIdRef.current = userId

  useEffect(() => {
    if (!userId) return
    const timer = setTimeout(() => {
      updateViewerPreferences(userId, {
        tooltipOptions,
        filterOptions,
        isSidebarCollapsed,
        viewLayout,
        minimapPreference,
        linkHighlights,
        showLiveProcessData,
        graphMode: graphModePreference,
        photoMode: photoModePreference,
      })
    }, 300)
    return () => clearTimeout(timer)
  }, [
    userId,
    tooltipOptions,
    filterOptions,
    isSidebarCollapsed,
    viewLayout,
    minimapPreference,
    linkHighlights,
    showLiveProcessData,
    graphModePreference,
    photoModePreference,
  ])

  useEffect(() => {
    return () => {
      const uid = userIdRef.current
      if (!uid) return
      const {
        tooltipOptions: t,
        filterOptions: f,
        isSidebarCollapsed: s,
        viewLayout: v,
        minimapPreference: minimap,
        linkHighlights: l,
        showLiveProcessData: live,
        graphMode: graph,
        photoMode: photo,
      } = uxPrefsRef.current
      updateViewerPreferences(uid, {
        tooltipOptions: t,
        filterOptions: f,
        isSidebarCollapsed: s,
        viewLayout: v,
        minimapPreference: minimap,
        linkHighlights: l,
        showLiveProcessData: live,
        graphMode: graph,
        photoMode: photo,
      })
    }
  }, [])

  const { hoverId, handleMouseEnter, handleMouseLeave } = useBoxHover()

  const isOnDocument = activeHistoryItem.type === 'document'
  const isOnLinkedDocuments =
    activeHistoryItem.type === 'search' &&
    activeHistoryItem.linkedDocuments !== undefined
  const isOnSearch = activeHistoryItem.type === 'search' && !isOnLinkedDocuments

  useEffect(() => {
    if (
      !shouldExitDocumentFullscreenOnLeave({
        isDocumentFullscreen,
        isOnDocument,
      })
    ) {
      return
    }
    setIsDocumentFullscreen(false)
  }, [isDocumentFullscreen, isOnDocument])

  const currentLinkedDocumentsData =
    activeHistoryItem.type === 'search'
      ? (activeHistoryItem.linkedDocuments ?? null)
      : null
  const currentDocumentId = isOnDocument ? activeHistoryItem.documentId : ''
  const currentPageNumber = isOnDocument ? activeHistoryItem.pageNumber : 1
  const currentSelectedBoxIds = useMemo(
    () => (isOnDocument ? activeHistoryItem.selectedBoxIds : []),
    [isOnDocument, activeHistoryItem]
  )
  const currentDocumentQuery = isOnDocument ? activeHistoryItem.query : ''
  const currentSearchOriginQuery = isOnDocument
    ? (activeHistoryItem.searchOriginQuery ?? '')
    : ''
  const defaultSearchState = useMemo(
    () => createDefaultSearchHistoryItem().searchState,
    []
  )
  const currentSearchState =
    activeHistoryItem.type === 'search'
      ? activeHistoryItem.searchState
      : defaultSearchState

  const handleSelectDocument = useCallback(
    (
      documentId: string,
      query?: string,
      fragmentHighlight?: FragmentHighlight,
      searchOriginQuery?: string,
      targetPageNumber?: number
    ) => {
      const pageNumber = targetPageNumber ?? fragmentHighlight?.pageNum ?? 1
      if (
        tooltipOptions.includes(TooltipOption.AutoOpenNewTab) &&
        isOnDocument &&
        currentDocumentId !== ''
      ) {
        addDocumentTab({
          documentId,
          pageNumber,
          query,
          searchOriginQuery,
          fragmentHighlight,
        })
      } else {
        pushToHistory(activeTabId, {
          type: 'document',
          documentId,
          pageNumber,
          selectedBoxIds: [],
          query: query ?? '',
          ...(searchOriginQuery && { searchOriginQuery }),
          ...(fragmentHighlight && { fragmentHighlight }),
        })
      }
    },
    [
      tooltipOptions,
      isOnDocument,
      currentDocumentId,
      addDocumentTab,
      pushToHistory,
      activeTabId,
    ]
  )

  const recordViewerAction = useCallback((action: string) => {
    setRecentViewerActions((prev) => {
      if (prev[prev.length - 1] === action) return prev
      return [...prev, action].slice(-MAX_RECENT_VIEWER_ACTIONS)
    })
  }, [])

  const contextValue = useMemo<KnowledgeDocumentUXContextType>(
    () => ({
      actionMode,
      setActionMode,
      selectedAssetType,
      setSelectedAssetType,
      groupDelimiter,
      setGroupDelimiter,
      hoverId,
      handleMouseEnter,
      handleMouseLeave,
      openTabs,
      addTab,
      addDocumentTab,
      removeTab,
      renameTab,
      reorderTabs,
      activeTabId,
      setActiveTabId,
      pagination,
      setPagination,
      activeHistoryItem,
      isOnDocument,
      isOnSearch,
      isOnLinkedDocuments,
      currentLinkedDocumentsData,
      currentDocumentId,
      currentPageNumber,
      currentSelectedBoxIds,
      currentDocumentQuery,
      currentSearchOriginQuery,
      currentSearchState,
      tooltipOptions,
      setTooltipOptions,
      linkHighlights,
      setLinkHighlights,
      showLiveProcessData,
      graphMode: canViewGraph && graphModePreference,
      setShowLiveProcessData,
      setGraphMode,
      canUsePhotoMode,
      photoMode: canUsePhotoMode && photoModePreference,
      setPhotoMode,
      showPhotoCounts,
      togglePhotoCounts,
      filterOptions,
      setFilterOptions,
      assetTypeFilters,
      setAssetTypeFilters,
      isAdvancedSearchOpen,
      setIsAdvancedSearchOpen,
      handleSelectDocument,
      isBrowserFullscreen,
      isDocumentFullscreen,
      setIsDocumentFullscreen,
      isSidebarCollapsed,
      setIsSidebarCollapsed,
      viewOnly,
      pushToHistory,
      goBack,
      goForward,
      registerTabLeaveGuard,
      updateDocumentHistoryItem,
      updateSearchHistoryItem,
      canGoBack,
      canGoForward,
      viewLayout,
      setViewLayout,
      minimapPreference,
      setMinimapPreference,
      isLabelerMode: isLabelerModeState,
      setIsLabelerMode,
      isEvalSessionLocked,
      onExitEvalSession,
      canViewGraph,
      isGeckoUser,
      canSeeSubjects,
      canViewUserEdits,
      canEditUserEdits,
      siteId,
      tabIndicatorColors,
      setTabIndicatorColors,
      recentViewerActions,
      recordViewerAction,
      metadataScope,
      setMetadataScope,
    }),
    [
      actionMode,
      selectedAssetType,
      groupDelimiter,
      hoverId,
      handleMouseEnter,
      handleMouseLeave,
      openTabs,
      addTab,
      addDocumentTab,
      removeTab,
      renameTab,
      reorderTabs,
      activeTabId,
      setActiveTabId,
      pagination,
      activeHistoryItem,
      isOnDocument,
      isOnSearch,
      isOnLinkedDocuments,
      currentLinkedDocumentsData,
      currentDocumentId,
      currentPageNumber,
      currentSelectedBoxIds,
      currentDocumentQuery,
      currentSearchOriginQuery,
      currentSearchState,
      tooltipOptions,
      linkHighlights,
      showLiveProcessData,
      graphModePreference,
      canUsePhotoMode,
      photoModePreference,
      showPhotoCounts,
      togglePhotoCounts,
      filterOptions,
      assetTypeFilters,
      isAdvancedSearchOpen,
      handleSelectDocument,
      isBrowserFullscreen,
      isDocumentFullscreen,
      isSidebarCollapsed,
      viewOnly,
      pushToHistory,
      goBack,
      goForward,
      registerTabLeaveGuard,
      updateDocumentHistoryItem,
      updateSearchHistoryItem,
      canGoBack,
      canGoForward,
      viewLayout,
      minimapPreference,
      isLabelerModeState,
      isEvalSessionLocked,
      setIsLabelerMode,
      onExitEvalSession,
      canViewGraph,
      isGeckoUser,
      canSeeSubjects,
      canViewUserEdits,
      canEditUserEdits,
      siteId,
      tabIndicatorColors,
      recentViewerActions,
      recordViewerAction,
      metadataScope,
      setMetadataScope,
    ]
  )

  return (
    <KnowledgeDocumentUXContext.Provider value={contextValue}>
      {children}
    </KnowledgeDocumentUXContext.Provider>
  )
}

const KnowledgeDocumentUXContext = createContext<
  KnowledgeDocumentUXContextType | undefined
>(undefined)

export const useKnowledgeDocumentUXContext = () => {
  const context = useContext(KnowledgeDocumentUXContext)
  if (context === undefined) {
    throw new Error(
      'useKnowledgeDocumentUXContext must be used within a KnowledgeDocumentUXProvider'
    )
  }
  return context
}

/** Document UX context when available; undefined outside a document viewer. */
export const useKnowledgeDocumentUXContextOptional = () => {
  return useContext(KnowledgeDocumentUXContext)
}
